import { createAccessControl } from 'better-auth/plugins/access'
import {
  adminAc,
  defaultStatements as adminDefaultStatements,
} from 'better-auth/plugins/admin/access'

// ============================================================
// ORGANIZATION PLUGIN - Access Control & Roles
// ============================================================

/**
 * Access Control Statement for Organization Plugin
 *
 * Defines resources and actions for organization management.
 * Uses `as const` for proper TypeScript inference.
 */
const orgStatement = {
  // Organization management
  organization: ['update', 'delete'],

  // Member management
  member: ['create', 'update', 'delete'],

  // Invitation management
  invitation: ['create', 'cancel'],

  // Access control management (for dynamic roles)
  ac: ['create', 'read', 'update', 'delete'],
} as const

/**
 * Access Control instance for Organization Plugin
 */
export const ac = createAccessControl(orgStatement)

/**
 * Organization Owner Role - Full control over the organization
 */
export const orgOwner = ac.newRole({
  organization: ['update', 'delete'],
  member: ['create', 'update', 'delete'],
  invitation: ['create', 'cancel'],
  ac: ['create', 'read', 'update', 'delete'],
})

/**
 * Organization Admin Role - Can manage organization but not delete it
 */
export const orgAdmin = ac.newRole({
  organization: ['update'],
  member: ['create', 'update', 'delete'],
  invitation: ['create', 'cancel'],
  ac: ['create', 'read', 'update', 'delete'],
})

/**
 * Organization Member Role - Read-only access
 */
export const orgMember = ac.newRole({
  // No permissions by default - read only
})

// ============================================================
// ADMIN PLUGIN - Access Control & Roles
// ============================================================

/**
 * Access Control Statement for Admin Plugin
 *
 * Merges the default admin plugin statements with any custom statements.
 * The default statements include 'user' and 'session' resources.
 */
const adminStatement = {
  ...adminDefaultStatements,
} as const

/**
 * Access Control instance for Admin Plugin
 */
export const adminAc = createAccessControl(adminStatement)

/**
 * Admin Role - Full control over user and session management
 */
export const admin = adminAc.newRole({
  ...adminAc.statements,
})

/**
 * User Role - Limited permissions (default for regular users)
 */
export const user = adminAc.newRole({
  // No admin permissions by default
})

// ============================================================
// Type Definitions
// ============================================================

/**
 * Organization role types for type safety
 */
export type OrgRole = 'owner' | 'admin' | 'member'

/**
 * Admin role types for type safety
 */
export type AdminRole = 'admin' | 'user'

/**
 * Helper function to check if an organization role has specific permissions
 * This is a client-side utility for quick checks
 */
export function roleHasPermission(
  role: string,
  resource: keyof typeof orgStatement,
  action: string,
): boolean {
  const roleMap = {
    owner: orgOwner,
    admin: orgAdmin,
    member: orgMember,
  }

  if (!(role in roleMap)) return false
  const rolePermissions = roleMap[role as keyof typeof roleMap]

  const resourcePermissions = rolePermissions.statements[resource]
  return resourcePermissions?.includes(action as never) ?? false
}
